import React, { useMemo } from 'react';
import { Box } from '@ublend-npm/aulaui-core';
import { WidgetPreference } from '@core/types/state';
import useBreakpoints from '@app/hooks/useBreakpoints';
import widgetsLibrary, { HOME_SPACES_WIDGET_IDS } from '../widgetsLibrary';
import SpaceWidgetSection from '../widgets/spaceWidget';
import {
  getActiveBreakpointKey,
  HOME_LAYOUT_BY_BREAKPOINT,
  HOME_PAGE_HORIZONTAL_PADDING,
  HOME_SIDEBAR_HEIGHT,
  HOME_SIDEBAR_STICKY_TOP,
  HOME_WIDGET_EXIT_MS,
  HOME_WIDGET_GRID_GAP_PX,
  HOME_WIDGET_TRANSITION_MS,
} from './widgetLayoutConstants';
import { MANAGE_WIDGET_ID, packMainLayout } from './packWidgets';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isQuickLinksInstitutionLevelEnabled } from '@core/constants/launchDarklyFeatureFlags';
import {
  isFixedWidget,
  isHideableWidget,
  PlacedWidget,
  WidgetLayoutProfile,
} from './widgetLayout.types';
import useFlipLayoutAnimation from './useFlipLayoutAnimation';
import useWidgetLayoutTransition from './useWidgetLayoutTransition';
import { WidgetRect } from './layoutFlip';

type HomeWidgetLayoutProps = {
  widgetPreferences: WidgetPreference[] | null;
};

const isWidgetVisible = (
  widgetId: string,
  widgetPreferences: WidgetPreference[] | null
): boolean => {
  if (isFixedWidget(widgetId)) {
    return true;
  }

  const preference = widgetPreferences?.find(
    (pref) => pref.widgetId === widgetId
  );

  return preference ? !preference.isHidden : true;
};

const getMainGridPlacement = (
  placed: PlacedWidget,
  heroVisible: boolean,
  extraRowOffset: number
): { gridColumn: string; gridRow: number } => ({
  gridColumn: `${placed.col + 1} / span ${placed.colSpan}`,
  gridRow: (heroVisible ? placed.row + 1 : placed.row) + extraRowOffset,
});

/** Enter / idle motion only — exit lives on the absolute overlay (no flex tween). */
const getWidgetMotionSx = (isEntering: boolean): Record<string, unknown> => {
  if (isEntering) {
    return {
      '@keyframes homeWidgetEnter': {
        from: { opacity: 0, transform: 'scale(0.96)' },
        to: { opacity: 1, transform: 'scale(1)' },
      },
      animation: `homeWidgetEnter ${HOME_WIDGET_TRANSITION_MS}ms cubic-bezier(0.2, 0, 0, 1)`,
    };
  }

  return {};
};

const HomeWidgetLayout = ({ widgetPreferences }: HomeWidgetLayoutProps) => {
  const flags = useFlags();
  const isQuickLinksEnabled = isQuickLinksInstitutionLevelEnabled(flags);
  const breakpoints = useBreakpoints();
  const breakpoint = getActiveBreakpointKey(breakpoints);
  const layoutConfig = HOME_LAYOUT_BY_BREAKPOINT[breakpoint];
  const mainGridRef = React.useRef<HTMLDivElement | null>(null);
  const exitOverlayRectsRef = React.useRef<Map<string, WidgetRect>>(new Map());
  const useFlexRows =
    breakpoint === 'xl' ||
    breakpoint === 'lg' ||
    breakpoint === 'md' ||
    breakpoint === 'sm';

  const layoutById = useMemo(
    () =>
      widgetsLibrary.reduce<Record<string, WidgetLayoutProfile>>(
        (acc, widget) => {
          acc[widget.id] = widget.layout;
          return acc;
        },
        {}
      ),
    []
  );

  const widgetsById = useMemo(
    () =>
      widgetsLibrary.reduce<Record<string, typeof widgetsLibrary[number]>>(
        (acc, widget) => {
          acc[widget.id] = widget;
          return acc;
        },
        {}
      ),
    []
  );

  const visibleHideableIds = useMemo(
    () =>
      widgetsLibrary
        .filter(
          (widget) =>
            isHideableWidget(widget.id) &&
            isWidgetVisible(widget.id, widgetPreferences) &&
            (widget.id !== 'home-quick-links-widget' || isQuickLinksEnabled)
        )
        .map((widget) => widget.id),
    [widgetPreferences, isQuickLinksEnabled]
  );

  const { exitingIds, enteringIds } =
    useWidgetLayoutTransition(visibleHideableIds);

  // Pack the immediate target layout from visible ids (do not wait on transition state).
  const packedLayout = useMemo(
    () =>
      packMainLayout({
        hideableWidgetIds: visibleHideableIds,
        layoutById,
        breakpoint,
      }),
    [visibleHideableIds, layoutById, breakpoint]
  );

  const animationKey = `${breakpoint}:${visibleHideableIds.join(',')}`;
  const isAnimating = exitingIds.size > 0 || enteringIds.size > 0;
  const layoutPositionsRef = useFlipLayoutAnimation(
    mainGridRef,
    animationKey,
    isAnimating
  );

  // Freeze First rects for exit overlays for the duration of the exit.
  if (exitingIds.size > 0) {
    exitingIds.forEach((widgetId) => {
      if (!exitOverlayRectsRef.current.has(widgetId)) {
        const measured = layoutPositionsRef.current.get(widgetId);
        if (measured) {
          exitOverlayRectsRef.current.set(widgetId, { ...measured });
        }
      }
    });
  } else if (exitOverlayRectsRef.current.size > 0) {
    exitOverlayRectsRef.current = new Map();
  }

  const showSpacesSection = HOME_SPACES_WIDGET_IDS.some((widgetId) =>
    isWidgetVisible(widgetId, widgetPreferences)
  );

  const recentUpdatesWidget = widgetsById['home-recent-updates-widget'];
  const stackedRecentUpdates = !layoutConfig.hasSidebar;

  const getFlexRowItemFlex = (cardsInRow: number): number | string => {
    // On XL/LG a solitary card should match half-row width (1 of 2), not stretch.
    if ((breakpoint === 'xl' || breakpoint === 'lg') && cardsInRow === 1) {
      return `0 0 calc((100% - ${HOME_WIDGET_GRID_GAP_PX}px) / 2)`;
    }

    return 1;
  };

  const renderWidgetSlot = (
    widgetId: string,
    options?: {
      flex?: number | string;
      gridPlacement?: { gridColumn: string; gridRow: number };
    }
  ) => {
    const widget = widgetsById[widgetId];
    if (!widget?.component) {
      return null;
    }

    const isEntering = enteringIds.has(widgetId);
    const isAnimated =
      isHideableWidget(widgetId) && widgetId !== MANAGE_WIDGET_ID;

    let flexSx: Record<string, unknown> = {};
    if (options?.flex !== undefined) {
      flexSx = { flex: options.flex };
    } else if (useFlexRows && !options?.gridPlacement) {
      flexSx = { flex: 1 };
    }

    return (
      <Box
        key={widgetId}
        id={widgetId}
        data-widget-id={widgetId}
        data-layout-animated={isAnimated ? 'true' : undefined}
        aria-label={widget.ariaLabel}
        sx={{
          minWidth: 0,
          ...(options?.gridPlacement ?? {}),
          ...flexSx,
          ...getWidgetMotionSx(isEntering),
        }}
      >
        {widget.component}
      </Box>
    );
  };

  const renderExitOverlays = () => {
    if (exitingIds.size === 0) {
      return null;
    }

    return Array.from(exitingIds).map((widgetId) => {
      const widget = widgetsById[widgetId];
      const rect = exitOverlayRectsRef.current.get(widgetId);
      if (!widget?.component || !rect) {
        return null;
      }

      return (
        <Box
          key={`exit-${widgetId}`}
          data-widget-id={widgetId}
          data-layout-exiting="true"
          aria-hidden
          sx={{
            position: 'fixed',
            left: `${rect.left}px`,
            top: `${rect.top}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            zIndex: 2,
            pointerEvents: 'none',
            overflow: 'hidden',
            '@keyframes homeWidgetExit': {
              from: { opacity: 1, transform: 'scale(1)' },
              to: { opacity: 0, transform: 'scale(0.96)' },
            },
            animation: `homeWidgetExit ${HOME_WIDGET_EXIT_MS}ms cubic-bezier(0.2, 0, 0, 1) forwards`,
          }}
        >
          {widget.component}
        </Box>
      );
    });
  };

  const renderXlFlexCanvas = () => (
    <Box
      ref={mainGridRef}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: `${HOME_WIDGET_GRID_GAP_PX}px`,
        minWidth: 0,
      }}
    >
      {showSpacesSection ? (
        <Box
          data-widget-id="home-hero-section"
          sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}
        >
          <SpaceWidgetSection
            showRoleToggle={isWidgetVisible(
              'home-switchrole-widget',
              widgetPreferences
            )}
            showGreeting={isWidgetVisible(
              'home-greeting-widget',
              widgetPreferences
            )}
            showSpaceCarousel={isWidgetVisible(
              'home-space-widget',
              widgetPreferences
            )}
          />
        </Box>
      ) : null}

      {stackedRecentUpdates && recentUpdatesWidget?.component ? (
        <Box
          data-widget-id={recentUpdatesWidget.id}
          id={recentUpdatesWidget.id}
          aria-label={recentUpdatesWidget.ariaLabel}
          sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}
        >
          {recentUpdatesWidget.component}
        </Box>
      ) : null}

      {packedLayout.widgetRows.map((row) => (
        <Box
          key={`row-${row.row}`}
          sx={{
            display: 'flex',
            gap: `${HOME_WIDGET_GRID_GAP_PX}px`,
            minWidth: 0,
          }}
        >
          {row.widgetIds.map((widgetId) =>
            renderWidgetSlot(widgetId, {
              flex: getFlexRowItemFlex(row.widgetIds.length),
            })
          )}
        </Box>
      ))}

      {renderExitOverlays()}
    </Box>
  );

  const renderGridCanvas = () => {
    const recentUpdatesRow = showSpacesSection ? 2 : 1;
    const mainWidgetRowOffset = stackedRecentUpdates ? 1 : 0;

    return (
      <Box
        ref={mainGridRef}
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `repeat(${layoutConfig.mainCols}, minmax(0, 1fr))`,
          gap: `${HOME_WIDGET_GRID_GAP_PX}px`,
          alignContent: 'start',
          alignItems: 'start',
          minWidth: 0,
        }}
      >
        {showSpacesSection ? (
          <Box
            data-widget-id="home-hero-section"
            sx={{
              gridColumn: '1 / -1',
              gridRow: 1,
              minWidth: 0,
            }}
          >
            <SpaceWidgetSection
              showRoleToggle={isWidgetVisible(
                'home-switchrole-widget',
                widgetPreferences
              )}
              showGreeting={isWidgetVisible(
                'home-greeting-widget',
                widgetPreferences
              )}
              showSpaceCarousel={isWidgetVisible(
                'home-space-widget',
                widgetPreferences
              )}
            />
          </Box>
        ) : null}

        {stackedRecentUpdates && recentUpdatesWidget?.component ? (
          <Box
            data-widget-id={recentUpdatesWidget.id}
            id={recentUpdatesWidget.id}
            aria-label={recentUpdatesWidget.ariaLabel}
            sx={{
              gridColumn: '1 / -1',
              gridRow: recentUpdatesRow,
              minWidth: 0,
            }}
          >
            {recentUpdatesWidget.component}
          </Box>
        ) : null}

        {packedLayout.gridPlacements.map((placed) => {
          const { gridColumn, gridRow } = getMainGridPlacement(
            placed,
            showSpacesSection,
            mainWidgetRowOffset
          );

          return renderWidgetSlot(placed.id, {
            gridPlacement: { gridColumn, gridRow },
          });
        })}

        {renderExitOverlays()}
      </Box>
    );
  };

  const renderMainCanvas = () =>
    useFlexRows ? renderXlFlexCanvas() : renderGridCanvas();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        px: HOME_PAGE_HORIZONTAL_PADDING,
        pb: '2rem',
        pt: '1.5rem',
        boxSizing: 'border-box',
      }}
    >
      {layoutConfig.hasSidebar ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${layoutConfig.totalCols}, minmax(0, 1fr))`,
            gap: `${HOME_WIDGET_GRID_GAP_PX}px`,
            alignItems: 'start',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              gridColumn: `1 / span ${layoutConfig.heroCols}`,
              minWidth: 0,
            }}
          >
            {renderMainCanvas()}
          </Box>

          {recentUpdatesWidget?.component ? (
            <Box
              sx={{
                gridColumn: `${layoutConfig.heroCols + 1} / span ${
                  layoutConfig.sidebarCols
                }`,
                minWidth: 0,
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                data-widget-id={recentUpdatesWidget.id}
                id={recentUpdatesWidget.id}
                data-home-recent-updates-sidebar
                aria-label={recentUpdatesWidget.ariaLabel}
                sx={{
                  position: 'sticky',
                  top: HOME_SIDEBAR_STICKY_TOP,
                  height: HOME_SIDEBAR_HEIGHT,
                  maxHeight: HOME_SIDEBAR_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                {recentUpdatesWidget.component}
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : (
        renderMainCanvas()
      )}
    </Box>
  );
};

export default HomeWidgetLayout;
